/**
 * Email de centralización de pedidos a proveedor — genera un `.eml`
 * descargable (cabecera `X-Unsent: 1` → Outlook lo abre como BORRADOR
 * editable) con un bloque por producto que replica el formato acordado
 * con el CEO: tabla de proveedor (azul), tabla de cliente (gris), cuadro
 * de margen/datos/notas y pie con cambio EUR/USD + transporte.
 *
 * El email NO va al proveedor: va a las personas de Approx que centralizan
 * los pedidos. Los destinatarios (To/CC) salen de la plantilla de email
 * "Pedido a proveedor (centralización)" (/admin/email-templates), editable.
 */
const REMITENTE_DEFAULT = 'jesus@approx.es';

export type PedidoEmailItem = {
  cliente_codigo?: string | null;
  cliente_nombre?: string | null;
  proveedor_nombre?: string | null;
  ref_proveedor?: string | null;
  unidades?: number | null;
  cost_usd?: number | string | null;
  foc?: number | string | null;
  main_specs?: string | null;
  marca_nombre?: string | null;
  sku_cliente?: string | null;
  ean?: string | null;
  coste_cliente?: number | string | null;
  descripcion?: string | null;
  margen_approx_estimado?: number | null;
  datos_proveedor?: string | null;
  notas?: string | null;
  cambio_usd_eur?: number | string | null;
  transporte_eur?: number | string | null;
  precio_contenedor?: number | string | null;
};

function fmtEur(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
}
function fmtUsd(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} $`;
}
function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${Number(v).toFixed(2).replace('.', ',')} %`;
}
function safe(s: string | null | undefined): string {
  const t = (s ?? '').toString().trim();
  return t || '—';
}
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function encodeHeader(s: string): string {
  // ¿ASCII puro (code points 0-127)? Si lo es, no hace falta codificar (RFC 2047).
  if ([...s].every((c) => c.charCodeAt(0) <= 0x7f)) return s;
  const utf8 = unescape(encodeURIComponent(s));
  return `=?UTF-8?B?${btoa(utf8)}?=`;
}
function uniq(arr: (string | null | undefined)[]): string[] {
  return Array.from(new Set(arr.map((s) => (s ?? '').toString().trim()).filter(Boolean)));
}

function buildSubject(items: PedidoEmailItem[]): string {
  const clientes = uniq(items.map((i) => i.cliente_codigo || i.cliente_nombre));
  const skus = items.map((i) => (i.sku_cliente ?? '').toString().trim()).filter(Boolean);
  const provs = uniq(items.map((i) => i.proveedor_nombre));
  const cli = clientes.join(' / ') || '—';
  const skuStr = skus.join(' / ') || '—';
  const provStr = provs.join(', ') || '—';
  return `Pedido ${cli} ${skuStr} - Proveedor ${provStr}`;
}

const TH = 'padding:7px 10px;border:1px solid #9ca3af;font-size:12px;text-transform:uppercase;letter-spacing:.03em;color:#1f2937;text-align:left;vertical-align:top;font-weight:bold;';
const TD = 'padding:7px 10px;border:1px solid #9ca3af;font-size:13px;color:#1f2937;vertical-align:top;';

function bloqueProducto(it: PedidoEmailItem, n: number): string {
  const cli = (it.cliente_codigo || it.cliente_nombre || 'CLIENTE').toString().toUpperCase();
  return `
  <p style="margin:22px 0 8px 0;background:#ffff00;display:inline-block;padding:2px 6px;font-weight:bold;font-family:Arial,Helvetica,sans-serif;">******* PRODUCTO ${n} *********</p>

  <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:100%;max-width:1100px;margin:0 0 10px 0;">
    <tr style="background:#cfe2f3;">
      <th style="${TH}width:16%;">PROVEEDOR</th>
      <th style="${TH}width:11%;">REF.</th>
      <th style="${TH}width:9%;">UNITS</th>
      <th style="${TH}width:12%;">COST (USD)</th>
      <th style="${TH}width:9%;">F.O.C.</th>
      <th style="${TH}width:43%;">MAIN SPECS</th>
    </tr>
    <tr>
      <td style="${TD}">${escapeHtml(safe(it.proveedor_nombre))}</td>
      <td style="${TD}">${escapeHtml(safe(it.ref_proveedor))}</td>
      <td style="${TD}text-align:right;">${it.unidades != null ? String(it.unidades) : '—'}</td>
      <td style="${TD}text-align:right;white-space:nowrap;">${escapeHtml(fmtUsd(it.cost_usd))}</td>
      <td style="${TD}text-align:right;white-space:nowrap;">${escapeHtml(fmtPct(it.foc != null ? Number(it.foc) : null))}</td>
      <td style="${TD}">${escapeHtml(safe(it.main_specs))}</td>
    </tr>
  </table>

  <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:100%;max-width:1100px;margin:0 0 10px 0;">
    <tr style="background:#d9d9d9;">
      <th style="${TH}width:16%;">MARCA</th>
      <th style="${TH}width:12%;">SKU ${escapeHtml(cli)}</th>
      <th style="${TH}width:10%;">EAN</th>
      <th style="${TH}width:12%;">COSTE ${escapeHtml(cli)}</th>
      <th style="${TH}width:50%;">DESCRIPCION</th>
    </tr>
    <tr>
      <td style="${TD}">${escapeHtml(safe(it.marca_nombre))}</td>
      <td style="${TD}">${escapeHtml(safe(it.sku_cliente))}</td>
      <td style="${TD}">${escapeHtml(safe(it.ean))}</td>
      <td style="${TD}text-align:right;white-space:nowrap;">${escapeHtml(fmtEur(it.coste_cliente))}</td>
      <td style="${TD}">${escapeHtml(safe(it.descripcion))}</td>
    </tr>
  </table>

  <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:100%;max-width:560px;margin:0 0 8px 0;">
    <tr>
      <td style="${TD}background:#f3f4f6;width:42%;font-weight:bold;">NOTAS</td>
      <td style="${TD}">${escapeHtml(safe(it.notas))}</td>
    </tr>
  </table>`;
}

function buildHtmlBody(items: PedidoEmailItem[]): string {
  const clientes = uniq(items.map((i) => i.cliente_codigo || i.cliente_nombre));
  const cli = clientes.join(' / ') || '—';
  const bloques = items.map((it, i) => bloqueProducto(it, i + 1)).join('\n');
  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<title>Pedido Approx</title>
</head>
<body style="margin:0;padding:24px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1f2937;">
  <p style="margin:0 0 6px 0;">Os paso confirmación de pedido de ${escapeHtml(cli)}.&nbsp;&nbsp;(${items.length} Producto${items.length === 1 ? '' : 's'})</p>
  ${bloques}
</body>
</html>`;
}

function buildPlainBody(items: PedidoEmailItem[]): string {
  const clientes = uniq(items.map((i) => i.cliente_codigo || i.cliente_nombre));
  const cli = clientes.join(' / ') || '—';
  const lineas: string[] = [
    `Os paso confirmación de pedido de ${cli}.  (${items.length} Producto${items.length === 1 ? '' : 's'})`,
    '',
  ];
  items.forEach((it, i) => {
    const c = (it.cliente_codigo || it.cliente_nombre || 'CLIENTE').toString().toUpperCase();
    lineas.push(`******* PRODUCTO ${i + 1} *********`);
    lineas.push(`PROVEEDOR: ${safe(it.proveedor_nombre)}  |  REF.: ${safe(it.ref_proveedor)}  |  UNITS: ${it.unidades ?? '—'}  |  COST (USD): ${fmtUsd(it.cost_usd)}  |  F.O.C.: ${fmtPct(it.foc != null ? Number(it.foc) : null)}`);
    lineas.push(`MAIN SPECS: ${safe(it.main_specs)}`);
    lineas.push(`MARCA: ${safe(it.marca_nombre)}  |  SKU ${c}: ${safe(it.sku_cliente)}  |  EAN: ${safe(it.ean)}  |  COSTE ${c}: ${fmtEur(it.coste_cliente)}`);
    lineas.push(`DESCRIPCION: ${safe(it.descripcion)}`);
    lineas.push(`NOTAS: ${safe(it.notas)}`);
    lineas.push('');
  });
  return lineas.join('\r\n');
}

export type PedidoEmailRecipients = { to?: string | null; cc?: string[] | null };

function buildEml(items: PedidoEmailItem[], rec?: PedidoEmailRecipients): string {
  const from = REMITENTE_DEFAULT;
  const to = (rec?.to ?? '').toString().trim();
  const cc = (rec?.cc ?? []).map((s) => (s ?? '').toString().trim()).filter(Boolean).join(', ');
  const subject = buildSubject(items);
  const html = buildHtmlBody(items);
  const text = buildPlainBody(items);
  const date = new Date().toUTCString();
  const altBoundary = `=_alt_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

  const altPart = [
    `--${altBoundary}`,
    'Content-Type: text/plain; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    text,
    '',
    `--${altBoundary}`,
    'Content-Type: text/html; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    html,
    '',
    `--${altBoundary}--`,
  ].join('\r\n');

  const headers = [
    'X-Unsent: 1',
    `From: ${from}`,
    `To: ${to}`,
  ];
  if (cc) headers.push(`Cc: ${cc}`);
  headers.push(
    `Subject: ${encodeHeader(subject)}`,
    `Date: ${date}`,
    'MIME-Version: 1.0',
    `Content-Type: multipart/alternative; boundary="${altBoundary}"`,
    '',
  );
  return [...headers, altPart, ''].join('\r\n');
}

export function downloadPedidoEml(items: PedidoEmailItem[], rec?: PedidoEmailRecipients): void {
  if (items.length === 0) return;
  const content = buildEml(items, rec);
  const blob = new Blob([content], { type: 'message/rfc822;charset=UTF-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = items.length === 1
    ? (items[0].sku_cliente || items[0].ref_proveedor || 'pedido').replace(/[^a-z0-9-]+/gi, '-').slice(0, 40)
    : `${items.length}-productos`;
  a.download = `pedido-approx-${slug}-${new Date().toISOString().slice(0, 10)}.eml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
